// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for retrieving event group configurations.
 * Provides functionality to fetch predefined sequences of events that
 * commonly occur together, such as shipping processes or maintenance
 * routines, along with their required properties and validation rules.
 */
import * as z from 'zod';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { eventGroupConfigurationSchema } from '@/asset/api/types';

/**
 * Schema for validating the API response when fetching event group configurations.
 * Transforms the response to provide a more intuitive structure with
 * the configurations array separated from metadata. Handles null responses
 * by providing an empty array for consistent processing.
 */
const eventGroupConfigurationsResponseSchema = z
  .object({
    data: z.array(eventGroupConfigurationSchema).nullable(),
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({
    ...response,
    configurations: data ?? [],
  }));

type EventGroupConfigurationsResponse = z.infer<
  typeof eventGroupConfigurationsResponseSchema
>;

/**
 * Retrieves all available event group configurations.
 * This function handles the API call to fetch predefined sequences of
 * events that commonly occur together, validating the response data
 * using Zod schemas. These configurations define structured processes
 * like shipping or maintenance routines that involve multiple steps.
 *
 * @returns Promise resolving to the list of event group configurations and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function getEventGroupConfigurations(): Promise<EventGroupConfigurationsResponse> {
  const url = '/v1/event-group-configuration';
  const response = await api.get(url);
  const result = await parseResponse(
    eventGroupConfigurationsResponseSchema,
    response.data,
    {
      schemaName: 'EventGroupConfigurations',
      endpoint: url,
    }
  );

  return result;
}
